import { Fragment, useState } from 'react'

import { Listbox, Transition } from '@headlessui/react'
import clsx from 'clsx'

import { SelectOptions } from '../types/global'
import { ChevronIcon } from './icons/Chevron'
import { TickIcon } from './icons/Tick'

interface SelectProps<T> {
  options: SelectOptions<T>
  value: T
  onChange?: (value: T) => void
  className?: string
  id?: string
}

interface OptionRenderPropArg {
  active: boolean
  selected: boolean
  disabled: boolean
}

export function Select<T extends string | number | string[] | undefined>({
  options,
  className,
  value,
  onChange,
  id,
  ...props
}: SelectProps<T>) {
  const selectedOption = options.find((option) => option.value === value)
  const defaultOption = options.find((option) => !!option.default)

  const [selected, setSelected] = useState(selectedOption || defaultOption || options[0])
  const selectedLabel = options.find(({ value }) => value === selected.value)?.label

  const handleSelect = (option: typeof options[0]) => {
    setSelected(option)
    onChange && onChange(option.value)
  }

  return (
    <div className={clsx('relative flex items-center w-full h-7', className)} id={id} {...props}>
      <Listbox value={selected} onChange={handleSelect}>
        {({ open }: { open: boolean }) => (
          <div className="relative w-full">
            <Listbox.Button
              className={clsx(
                'relative flex items-center transition-colors w-full h-7 pt-0.5 text-left focus:outline-none focus:text-white bg-transparent border-t-0 border-x-0 border-b rounded-none appearance-none px-0.5 pb-px text-xl min-h-0 font-sans',
                open ? 'border-b-transparent duration-200' : 'border-b-white duration-300',
              )}>
              <span className="block truncate">{selectedLabel}</span>
              <ChevronIcon
                className="absolute pointer-events-none w-4 right-0.5 fill-current"
                aria-hidden="true"
              />{' '}
            </Listbox.Button>
            <Transition
              as={Fragment}
              enter="transition duration-200 ease-out"
              enterFrom="opacity-0 -translate-y-2"
              enterTo="opacity-100 translate-y-1"
              leave="transition duration-200 ease-out"
              leaveFrom="opacity-100"
              leaveTo="opacity-0">
              <Listbox.Options className="absolute w-full py-1 overflow-auto text-base bg-white rounded-md shadow-lg min-w-fit ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                {options.map((option, index) => (
                  <Listbox.Option
                    key={`${option.value} + ${index}`}
                    className={({ active }) =>
                      `relative cursor-default select-none py-2 pl-8 pr-4 ${
                        active ? 'bg-yellow-light text-navy' : 'text-black'
                      }`
                    }
                    value={option}>
                    {({ selected }: OptionRenderPropArg) => (
                      <>
                        <span className={`block text-black ${selected ? 'font-medium' : 'font-normal'}`}>
                          {option.label}
                        </span>
                        {selected ? (
                          <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-navy/50">
                            <TickIcon className="w-3 h-3 stroke-2" aria-hidden="true" />
                          </span>
                        ) : null}
                      </>
                    )}
                  </Listbox.Option>
                ))}
              </Listbox.Options>
            </Transition>
          </div>
        )}
      </Listbox>
    </div>
  )
}
